/**
 * MEC design system — Apple-idiom primitives on top of the Brand Guidelines (Dirección A):
 * one family (Inter), cool neutrals, one blue, flat surfaces, generous whitespace,
 * one idea per section, product imagery carries the story.
 */
import { forwardRef, type ReactNode, type ComponentProps, type HTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ---------- Section: full-bleed band with controlled tone + rhythm ---------- */
type Tone = 'white' | 'grey' | 'dark' | 'black';
const toneClass: Record<Tone, string> = {
  white: 'bg-white text-text-primary',
  grey: 'bg-bg-light text-text-primary',
  dark: 'bg-bg-dark text-text-light',
  black: 'bg-[#000] text-text-light',
};

export const Section = forwardRef<HTMLElement, { tone?: Tone; className?: string; children: ReactNode } & ComponentProps<'section'>>(
  function Section({ tone = 'white', className, children, ...rest }, ref) {
    return (
      <section ref={ref} className={cn('w-full py-24 sm:py-28 lg:py-36', toneClass[tone], className)} {...rest}>
        {children}
      </section>
    );
  }
);

export function Container({ className, children, narrow }: { className?: string; children: ReactNode; narrow?: boolean }) {
  return (
    <div className={cn('mx-auto w-full px-6 sm:px-8', narrow ? 'max-w-3xl' : 'max-w-[1120px]', className)}>
      {children}
    </div>
  );
}

/* ---------- Type ---------- */
export function Eyebrow({ children, className, dark, ...rest }: { children: ReactNode; className?: string; dark?: boolean } & HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn('text-sm sm:text-base font-semibold tracking-tight', dark ? 'text-brand-light' : 'text-brand', className)} {...rest}>
      {children}
    </p>
  );
}

export function Display({
  children,
  className,
  as: Tag = 'h2',
  size = 'lg',
  ...rest
}: { children: ReactNode; className?: string; as?: 'h1' | 'h2' | 'h3'; size?: 'xl' | 'lg' | 'md' } & HTMLAttributes<HTMLHeadingElement>) {
  const sizes = {
    xl: 'text-5xl sm:text-6xl lg:text-7xl xl:text-[88px]',
    lg: 'text-4xl sm:text-5xl lg:text-6xl',
    md: 'text-3xl sm:text-4xl lg:text-5xl',
  };
  return (
    <Tag className={cn('font-heading font-semibold tracking-[-0.03em] leading-[1.02]', sizes[size], className)} {...rest}>
      {children}
    </Tag>
  );
}

export function Lead({ children, className, dark, ...rest }: { children: ReactNode; className?: string; dark?: boolean } & HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn('text-lg sm:text-xl lg:text-2xl leading-snug font-medium', dark ? 'text-text-muted' : 'text-text-secondary', className)} {...rest}>
      {children}
    </p>
  );
}

/* ---------- Links & buttons ---------- */
export function CtaLink({ to, children, className, dark, external }: { to: string; children: ReactNode; className?: string; dark?: boolean; external?: boolean }) {
  const cls = cn(
    'group inline-flex items-center gap-1 text-lg font-medium',
    dark ? 'text-brand-light' : 'text-brand',
    'hover:underline underline-offset-4',
    className
  );
  const inner = (
    <>
      {children}
      <ChevronRight className="w-4 h-4 translate-y-px transition-transform group-hover:translate-x-0.5" />
    </>
  );
  return external ? (
    <a href={to} className={cls} target="_blank" rel="noopener noreferrer">{inner}</a>
  ) : (
    <Link to={to} className={cls}>{inner}</Link>
  );
}

export function Pill({ to, children, className, onClick, variant = 'solid', dark }: {
  to?: string; children: ReactNode; className?: string; onClick?: () => void; variant?: 'solid' | 'ghost'; dark?: boolean;
}) {
  const cls = cn(
    'inline-flex items-center justify-center rounded-full px-6 h-12 text-[17px] font-medium transition-colors',
    variant === 'solid'
      ? 'bg-brand text-white hover:bg-brand-deep'
      : dark
        ? 'border border-white/25 text-text-light hover:bg-white/10'
        : 'border border-text-primary/20 text-text-primary hover:bg-text-primary/5',
    className
  );
  if (to) return <Link to={to} className={cls}>{children}</Link>;
  return <button type="button" onClick={onClick} className={cls}>{children}</button>;
}

/* ---------- Tile (bento) ---------- */
export function Tile({ className, children, dark, ...rest }: { className?: string; children: ReactNode; dark?: boolean } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('rounded-[28px] p-8 sm:p-10 flex flex-col', dark ? 'bg-white/[0.06] text-text-light' : 'bg-white text-text-primary', className)} {...rest}>
      {children}
    </div>
  );
}

/* ---------- Phone: CSS bezel around a real screenshot (never AI-rendered UI) ---------- */
export function Phone({ src, alt, className, priority, width = 300 }: { src: string; alt: string; className?: string; priority?: boolean; width?: number }) {
  // Screenshots are 601x1300 (iPhone 13 Pro Max aspect ≈ 0.462).
  return (
    <div
      className={cn('relative shrink-0 rounded-[44px] bg-[#0b0b0d] p-[10px] shadow-phone ring-1 ring-black/40', className)}
      style={{ width }}
    >
      <div className="relative overflow-hidden rounded-[36px] bg-black" style={{ aspectRatio: '601 / 1300' }}>
        <img
          src={src}
          alt={alt}
          width={601}
          height={1300}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
    </div>
  );
}

/* ---------- Stat ---------- */
export function Stat({ value, label, dark }: { value: string; label: string; dark?: boolean }) {
  return (
    <div>
      <div className={cn('font-heading text-5xl sm:text-6xl font-semibold tracking-[-0.04em] tabular-nums', dark ? 'text-text-light' : 'text-text-primary')}>{value}</div>
      <div className={cn('mt-2 text-base', dark ? 'text-text-muted' : 'text-text-secondary')}>{label}</div>
    </div>
  );
}

/* ---------- Browser frame: CSS window chrome around a real web screenshot ---------- */
export function BrowserFrame({ src, alt, width, height, className, priority }: { src: string; alt: string; width: number; height: number; className?: string; priority?: boolean }) {
  return (
    <div className={cn('rounded-[20px] bg-white shadow-phone ring-1 ring-black/10 overflow-hidden', className)}>
      <div className="h-10 flex items-center gap-2 px-4 bg-bg-light hairline">
        <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
        <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
        <span className="w-3 h-3 rounded-full bg-[#28c840]" />
        <span className="mx-auto h-6 w-1/2 rounded-md bg-white/80 ring-1 ring-black/5 text-[11px] text-text-tertiary flex items-center justify-center">app.mienviocuba.com</span>
      </div>
      <img src={src} alt={alt} width={width} height={height} loading={priority ? 'eager' : 'lazy'} decoding="async" className="w-full h-auto block" />
    </div>
  );
}
